import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { carouselImages } from '../data/media';
export function ClassCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const scrollTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(carouselImages.length - 1, index));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (slide) {
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft,
        behavior: 'smooth'
      });
    }
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const slides = Array.from(track.children) as HTMLElement[];
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let best = Number.POSITIVE_INFINITY;
      slides.forEach((slide, index) => {
        const mid = slide.offsetLeft - track.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(mid - center);
        if (distance < best) {
          best = distance;
          closest = index;
        }
      });
      setActive(closest);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  return <section className="bg-cream px-4 py-20 sm:py-28" aria-roledescription="carousel" aria-label="A look inside the system" data-id="element-42" data-mp="3r0k60xfvigq">
      <div className="mx-auto max-w-6xl" data-id="element-43" data-mp="cqdq4jmctowp">
        <h2 className="text-center font-serif text-4xl font-semibold text-espresso sm:text-5xl" data-id="element-44" data-mp="cplwhecbi192">
          A Look Inside the System
        </h2>
        <div className="relative mt-12" data-id="element-45" data-mp="hiyh7ddvlnwj">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-46" data-mp="3g62s3dzc8fq6">
            {carouselImages.map((image, index) => <div key={image.src} className="w-[82%] shrink-0 snap-center sm:w-[46%] lg:w-[31.5%]" aria-roledescription="slide" aria-label={`${index + 1} of ${carouselImages.length}`} data-id="element-47" data-mp="s9dun63kroy7" data-mpi={index}>
                <img src={image.src} alt={image.alt} loading="lazy" className="w-full rounded-2xl border border-champagne bg-cream-light shadow-md" data-id="element-48" data-mp="x2ytf0czsmyg" />
              </div>)}
          </div>
          <button type="button" onClick={() => scrollTo(active - 1)} disabled={active === 0} aria-label="Previous image" className="absolute left-0 top-1/2 hidden h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-espresso text-white shadow-lg transition-[opacity,background-color] duration-150 hover:bg-cocoa focus:outline-none focus-visible:ring-4 focus-visible:ring-espresso/25 disabled:opacity-0 sm:flex" data-id="element-49" data-mp="iec5495e0kv3">
            <ChevronLeftIcon size={20} data-id="element-50" data-mp="21djtsnzo01tt" />
          </button>
          <button type="button" onClick={() => scrollTo(active + 1)} disabled={active === carouselImages.length - 1} aria-label="Next image" className="absolute right-0 top-1/2 hidden h-11 w-11 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-espresso text-white shadow-lg transition-[opacity,background-color] duration-150 hover:bg-cocoa focus:outline-none focus-visible:ring-4 focus-visible:ring-espresso/25 disabled:opacity-0 sm:flex" data-id="element-51" data-mp="1nd90zfesod60">
            <ChevronRightIcon size={20} data-id="element-52" data-mp="2b8k4nkta7w0b" />
          </button>
        </div>
        <div className="mt-6 flex justify-center gap-2" data-id="element-53" data-mp="2yyxxvenls0wh">
          {carouselImages.map((image, index) => <button key={image.src} type="button" onClick={() => scrollTo(index)} aria-label={`Go to image ${index + 1}`} aria-current={active === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out ${active === index ? 'w-7 bg-raspberry' : 'w-2.5 bg-champagne hover:bg-champagne-deep'}`} data-id="element-54" data-mp="33rhbgbmaiw1a" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}